'use client';

import { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Crown, Palette, Zap, Scissors, Paintbrush, AlignEndHorizontal, ChevronDown, X } from 'lucide-react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { cn } from '@/lib/utils';

gsap.registerPlugin(ScrollTrigger);

const procedures = [
    {
        id: 'veneers',
        icon: Crown,
        titleKey: 'procedures.treatments.veneers.title',
        taglineKey: 'procedures.treatments.veneers.tagline',
        attributesKey: 'procedures.treatments.veneers.attributes',
        descriptionKey: 'procedures.treatments.veneers.description',
        lifespanKey: 'procedures.treatments.veneers.lifespan',
        idealForKey: 'procedures.treatments.veneers.idealFor',
        keyFeaturesKey: 'procedures.treatments.veneers.keyFeatures'
    },
    {
        id: 'crowns',
        icon: Palette,
        titleKey: 'procedures.treatments.crowns.title',
        taglineKey: 'procedures.treatments.crowns.tagline',
        attributesKey: 'procedures.treatments.crowns.attributes',
        descriptionKey: 'procedures.treatments.crowns.description',
        lifespanKey: 'procedures.treatments.crowns.lifespan',
        idealForKey: 'procedures.treatments.crowns.idealFor',
        keyFeaturesKey: 'procedures.treatments.crowns.keyFeatures'
    },
    {
        id: 'whitening',
        icon: Zap,
        titleKey: 'procedures.treatments.whitening.title',
        taglineKey: 'procedures.treatments.whitening.tagline',
        attributesKey: 'procedures.treatments.whitening.attributes',
        descriptionKey: 'procedures.treatments.whitening.description',
        lifespanKey: 'procedures.treatments.whitening.lifespan',
        idealForKey: 'procedures.treatments.whitening.idealFor',
        keyFeaturesKey: 'procedures.treatments.whitening.keyFeatures'
    },
    {
        id: 'contouring',
        icon: Scissors,
        titleKey: 'procedures.treatments.contouring.title',
        taglineKey: 'procedures.treatments.contouring.tagline',
        attributesKey: 'procedures.treatments.contouring.attributes',
        descriptionKey: 'procedures.treatments.contouring.description',
        lifespanKey: 'procedures.treatments.contouring.lifespan',
        idealForKey: 'procedures.treatments.contouring.idealFor',
        keyFeaturesKey: 'procedures.treatments.contouring.keyFeatures'
    },
    {
        id: 'bonding',
        icon: Paintbrush,
        titleKey: 'procedures.treatments.bonding.title',
        taglineKey: 'procedures.treatments.bonding.tagline',
        attributesKey: 'procedures.treatments.bonding.attributes',
        descriptionKey: 'procedures.treatments.bonding.description',
        lifespanKey: 'procedures.treatments.bonding.lifespan',
        idealForKey: 'procedures.treatments.bonding.idealFor',
        keyFeaturesKey: 'procedures.treatments.bonding.keyFeatures'
    },
    {
        id: 'orthodontics',
        icon: AlignEndHorizontal,
        titleKey: 'procedures.treatments.orthodontics.title',
        taglineKey: 'procedures.treatments.orthodontics.tagline',
        attributesKey: 'procedures.treatments.orthodontics.attributes',
        descriptionKey: 'procedures.treatments.orthodontics.description',
        lifespanKey: 'procedures.treatments.orthodontics.lifespan',
        idealForKey: 'procedures.treatments.orthodontics.idealFor',
        keyFeaturesKey: 'procedures.treatments.orthodontics.keyFeatures'
    }
];

export default function ProceduresSection() {
    const { t } = useTranslation('hollywood-smile');
    const { isRTL } = useRTL();
    const [expandedCard, setExpandedCard] = useState<string | null>(null);
    const [containerHeight, setContainerHeight] = useState<number>(600);
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const introRef = useRef<HTMLParagraphElement>(null);
    const cardsRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const expandedCardRef = useRef<HTMLDivElement>(null);

    // Calculate container height based on grid content
    useEffect(() => {
        if (cardsRef.current) {
            const updateHeight = () => {
                const rect = cardsRef.current?.getBoundingClientRect();
                if (rect) {
                    // Always calculate based on grid layout, not expansion state
                    const baseHeight = Math.max(600, rect.height + 20);
                    setContainerHeight(baseHeight);
                }
            };

            // Only update height when not expanded to prevent layout shifts
            if (!expandedCard) {
                updateHeight();
                window.addEventListener('resize', updateHeight);

                // Update height after grid renders
                const timer = setTimeout(updateHeight, 100);

                return () => {
                    window.removeEventListener('resize', updateHeight);
                    clearTimeout(timer);
                };
            }
        }
    }, [expandedCard]);

    // Click outside handler to close expanded card
    useEffect(() => {
        if (!expandedCard) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (expandedCardRef.current && !expandedCardRef.current.contains(event.target as Node)) {
                closeCard();
            }
        };

        // Add event listener with a small delay to prevent immediate closure
        const timer = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
        }, 100);

        return () => {
            clearTimeout(timer);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [expandedCard]);

    // GSAP animations - tied to scroll position
    useEffect(() => {
        const ctx = gsap.context(() => {
            // Set initial states for all animated elements
            gsap.set([titleRef.current, introRef.current], { opacity: 0, y: 50 });
            if (cardsRef.current?.children) {
                gsap.set(cardsRef.current.children, { opacity: 0, y: 60, scale: 0.9 });
            }

            // Title animation - tied to scroll
            gsap.to(titleRef.current, {
                opacity: 1,
                y: 0,
                duration: 1,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: titleRef.current,
                    start: "top 80%",
                    end: "bottom 20%",
                    toggleActions: "play none none reverse",
                    scrub: false
                }
            });

            // Introduction animation - tied to scroll
            gsap.to(introRef.current, {
                opacity: 1,
                y: 0,
                duration: 0.8,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: introRef.current,
                    start: "top 75%",
                    end: "bottom 25%",
                    toggleActions: "play none none reverse",
                    scrub: false
                }
            });

            // Individual card animations - each card appears based on its own scroll position
            if (cardsRef.current?.children) {
                Array.from(cardsRef.current.children).forEach((card, index) => {
                    gsap.to(card, {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 1,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: card,
                            start: "top 85%", // Card becomes visible when its top hits 85% of viewport
                            end: "bottom 15%",
                            toggleActions: "play none none reverse",
                            scrub: false
                        }
                    });
                });
            }

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    const toggleCard = (cardId: string) => {
        setExpandedCard(expandedCard === cardId ? null : cardId);
    };

    const closeCard = () => {
        setExpandedCard(null);
    };

    return (
        <section
            id="procedures"
            ref={sectionRef}
            className="py-14 bg-white pb-40 dark:bg-slate-800 relative overflow-hidden"
        >
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-20 left-10 w-64 h-64 bg-gold rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-80 h-80 bg-gold rounded-full blur-2xl"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-gold/20 rounded-full blur-3xl"></div>
            </div>

            <div className="container relative">
                {/* Section Header */}
                <div className="text-center max-w-4xl mx-auto">
                    <h2
                        ref={titleRef}
                        className="text-4xl md:text-5xl lg:text-6xl font-bold text-navy dark:text-white mb-8 font-serif opacity-0"
                        style={{ transform: 'translateY(50px)' }}
                    >
                        {t('procedures.title.part1')}{' '}
                        <span className="text-gold relative">
                            {t('procedures.title.part2')}
                            <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gold rounded-full"></div>
                        </span>
                    </h2>
                    <p
                        ref={introRef}
                        className="text-xl text-slate-600 dark:text-slate-300 leading-relaxed opacity-0"
                        style={{ transform: 'translateY(50px)' }}
                    >
                        {t('procedures.subtitle')}
                    </p>
                </div>

                {/* Cards Container with Fixed Height */}
                <div
                    ref={containerRef}
                    className="relative w-full max-w-7xl mx-auto"
                    style={{
                        height: `${containerHeight}px`,
                        minHeight: `${containerHeight}px`
                    }}
                >
                    <LayoutGroup>
                        {/* Grid View */}
                        <AnimatePresence mode="wait">
                            {!expandedCard && (
                                <motion.div
                                    key="grid-view"
                                    ref={cardsRef}
                                    initial={false}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{
                                        duration: 0.3,
                                        ease: "easeInOut"
                                    }}
                                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 auto-rows-fr w-full opacity-0"
                                    style={{ transform: 'translateY(60px) scale(0.9)' }}
                                >
                                    {procedures.map((procedure, index) => (
                                        <motion.div
                                            key={`grid-${procedure.id}`}
                                            layoutId={`card-${procedure.id}`}
                                            whileHover={{ scale: 1.02, y: -5 }}
                                            transition={{
                                                type: "spring",
                                                stiffness: 300,
                                                damping: 30
                                            }}
                                            className="h-full cursor-pointer"
                                            onClick={() => toggleCard(procedure.id)}
                                        >
                                            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-gold/50 dark:hover:border-gold/50 transition-all duration-300 shadow-lg hover:shadow-2xl group overflow-hidden h-full flex flex-col">
                                                <CardContent className="p-0 flex flex-col h-full">
                                                    {/* Card Header */}
                                                    <div className="p-6 pb-4 flex-1 flex flex-col">
                                                        <div className={cn(
                                                            "flex items-center mb-4",
                                                            isRTL ? "space-x-reverse space-x-4" : "space-x-4"
                                                        )}>
                                                            <div className="w-16 h-16 bg-gold rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300">
                                                                <procedure.icon className="w-8 h-8 text-white" />
                                                            </div>
                                                            <div className="flex-1">
                                                                <h3 className="text-2xl font-bold text-navy dark:text-white group-hover:text-gold dark:group-hover:text-gold transition-colors duration-300">
                                                                    {t(procedure.titleKey)}
                                                                </h3>
                                                            </div>
                                                        </div>

                                                        {/* Subtle Divider */}
                                                        <div className="w-full h-px bg-gold mb-4"></div>

                                                        {/* Tagline - Fixed Height */}
                                                        <div className="h-14 mb-4 flex items-start">
                                                            <p className="text-lg font-medium text-navy dark:text-slate-300 leading-relaxed overflow-hidden" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                                                {t(procedure.taglineKey)}
                                                            </p>
                                                        </div>

                                                        {/* Key Attributes Pills - Always at same level */}
                                                        <div className="flex flex-wrap gap-2 mb-6">
                                                            {(() => {
                                                            const attributes = t(procedure.attributesKey, { returnObjects: true });
                                                            if (Array.isArray(attributes)) {
                                                                return attributes.map((attribute: string, attrIndex: number) => (
                                                                    <span
                                                                        key={attrIndex}
                                                                        className="px-3 py-1 bg-gold/10 border border-gold/30 rounded-full text-sm font-medium text-navy dark:text-gold whitespace-nowrap"
                                                                    >
                                                                        {attribute}
                                                                    </span>
                                                                ));
                                                            }
                                                            return null;
                                                        })()}
                                                        </div>
                                                    </div>

                                                    {/* Learn More Button */}
                                                    <div className="p-6 pt-0 mt-auto">
                                                        <Button
                                                            className="w-full bg-gold hover:bg-gold-600 text-navy font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-gold hover:border-gold-600"
                                                        >
                                                            {t('procedures.labels.learnMore')}
                                                            <ChevronDown className={cn(
                                                                "w-5 h-5",
                                                                isRTL ? "mr-2" : "ml-2"
                                                            )} />
                                                        </Button>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </motion.div>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Expanded Drawer View - Absolute positioned to not affect layout */}
                        <AnimatePresence>
                            {expandedCard && (() => {
                                const selectedProcedure = procedures.find(procedure => procedure.id === expandedCard);
                                if (!selectedProcedure) return null;

                                return (
                                    <motion.div
                                        key={`expanded-${expandedCard}`}
                                        ref={expandedCardRef}
                                        layoutId={`card-${expandedCard}`}
                                        className="absolute inset-0 z-50"
                                        style={{ height: `${Math.min(containerHeight, 483)}px` }}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{
                                            type: "spring",
                                            stiffness: 300,
                                            damping: 30,
                                            opacity: { duration: 0.2 }
                                        }}
                                    >
                                        <Card className="bg-white dark:bg-slate-800 border-gold dark:border-gold shadow-2xl w-full h-full overflow-hidden">
                                            <CardContent className="p-0 h-full flex flex-col">
                                                {/* Header - Fixed */}
                                                <div className="p-3 pb-2 border-b border-slate-200 dark:border-slate-700 relative shrink-0">
                                                    {/* Close Button */}
                                                    <motion.button
                                                        initial={{ opacity: 0, scale: 0.8 }}
                                                        animate={{ opacity: 1, scale: 1 }}
                                                        transition={{ delay: 0.2 }}
                                                        onClick={closeCard}
                                                        className="absolute top-3 right-3 w-8 h-8 bg-gold hover:bg-gold-600 rounded-full flex items-center justify-center text-white transition-colors z-20"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </motion.button>

                                                    <div className={cn(
                                                        "flex items-center mb-2 pr-12",
                                                        isRTL ? "space-x-reverse space-x-4" : "space-x-4"
                                                    )}>
                                                        <div className="w-12 h-12 bg-gold rounded-2xl flex items-center justify-center shrink-0">
                                                            <selectedProcedure.icon className="w-6 h-6 text-white" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <h3 className="text-xl font-bold text-navy dark:text-white mb-1">
                                                                {t(selectedProcedure.titleKey)}
                                                            </h3>
                                                            <p className="text-sm text-gold dark:text-gold font-medium">
                                                                {t(selectedProcedure.taglineKey)}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* Key Attributes */}
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {(() => {
                                                            const attributes = t(selectedProcedure.attributesKey, { returnObjects: true });
                                                            if (Array.isArray(attributes)) {
                                                                return attributes.map((attribute: string, attrIndex: number) => (
                                                                    <span
                                                                        key={attrIndex}
                                                                        className="px-2 py-1 bg-gold/20 border border-gold/50 rounded-full text-xs font-medium text-navy dark:text-gold"
                                                                    >
                                                                        {attribute}
                                                                    </span>
                                                                ));
                                                            }
                                                            return null;
                                                        })()}
                                                    </div>
                                                </div>

                                                {/* Content - Scrollable */}
                                                <div className="p-3 pt-2 overflow-y-auto flex-1 space-y-3 text-sm pb-2">
                                                    {/* Description */}
                                                    <motion.div
                                                        initial={{ opacity: 0, y: 20 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ delay: 0.3 }}
                                                    >
                                                        <h4 className="font-semibold text-navy dark:text-white mb-1.5">
                                                            {t('procedures.labels.overview')}
                                                        </h4>
                                                        <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                                                            {t(selectedProcedure.descriptionKey)}
                                                        </p>
                                                    </motion.div>

                                                    {/* Details Grid */}
                                                    <div className="grid md:grid-cols-2 gap-3">
                                                        {/* Lifespan */}
                                                        <motion.div
                                                            initial={{ opacity: 0, y: 20 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            transition={{ delay: 0.4 }}
                                                            className="bg-gold/5 rounded-xl p-2.5 border border-gold/20"
                                                        >
                                                            <h4 className="font-semibold text-navy dark:text-white mb-1.5">
                                                                {t('procedures.labels.expectedLifespan')}
                                                            </h4>
                                                            <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                                                                {t(selectedProcedure.lifespanKey)}
                                                            </p>
                                                        </motion.div>

                                                        {/* Ideal For */}
                                                        <motion.div
                                                            initial={{ opacity: 0, y: 20 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            transition={{ delay: 0.5 }}
                                                            className="bg-gold/5 rounded-xl p-2.5 border border-gold/20"
                                                        >
                                                            <h4 className="font-semibold text-navy dark:text-white mb-1.5">
                                                                {t('procedures.labels.idealFor')}
                                                            </h4>
                                                            <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                                                                {t(selectedProcedure.idealForKey)}
                                                            </p>
                                                        </motion.div>
                                                    </div>

                                                    {/* Key Features */}
                                                    <motion.div
                                                        initial={{ opacity: 0, y: 20 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ delay: 0.6 }}
                                                        className="pb-1"
                                                    >
                                                        <h4 className="font-semibold text-navy dark:text-white mb-2">
                                                            {t('procedures.labels.keyFeatures')}
                                                        </h4>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                                                            {(() => {
                                                                const keyFeatures = t(selectedProcedure.keyFeaturesKey, { returnObjects: true });
                                                                if (Array.isArray(keyFeatures)) {
                                                                    return keyFeatures.map((feature: string, index: number) => (
                                                                        <motion.div
                                                                            key={index}
                                                                            initial={{ opacity: 0, x: -20 }}
                                                                            animate={{ opacity: 1, x: 0 }}
                                                                            transition={{ delay: 0.7 + index * 0.1 }}
                                                                            className={cn(
                                                                                "flex items-center bg-white dark:bg-navy-950 rounded-lg p-2 border border-slate-200 dark:border-slate-700",
                                                                                isRTL ? "space-x-reverse space-x-2" : "space-x-2"
                                                                            )}
                                                                        >
                                                                            <div className="w-1.5 h-1.5 bg-gold rounded-full shrink-0"></div>
                                                                            <span className="text-sm">{feature}</span>
                                                                        </motion.div>
                                                                    ));
                                                                }
                                                                return null;
                                                            })()}
                                                        </div>
                                                    </motion.div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                );
                            })()}
                        </AnimatePresence>
                    </LayoutGroup>
                </div>
            </div>
        </section>
    );
} 
